import { db } from '../firebase';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  addDoc, 
  deleteDoc, 
  getDocs, 
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';
// Rate limiting removed - moved to admin app
import { auth } from '../firebase';

// ----- User Data Management -----

/**
 * Creates or updates a user's profile in Firestore
 * @param {Object} user - The Firebase Auth user object
 * @param {Object} additionalData - Optional additional user data
 * @returns {Promise<void>}
 */
export const saveUserProfile = async (user, additionalData = {}) => {
  if (!user || !user.uid) return;
  
  // Rate limiting removed for better user experience
  
  try {
    // Don't save guest user profiles
    if (user.isGuest) return;
    
    const userRef = doc(db, "users", user.uid);
    
    // Use setDoc with merge instead of checking existence first
    // This avoids potential permission issues with getDoc
    await setDoc(userRef, {
      uid: user.uid,
      displayName: user.displayName || additionalData.displayName || null,
      email: user.email || additionalData.email || null,
      photoURL: user.photoURL || additionalData.photoURL || null,
      lastLogin: serverTimestamp(),
      ...additionalData,
      // Only set createdAt if document doesn't exist
      ...(additionalData.createdAt ? {} : { createdAt: serverTimestamp() })
    }, { merge: true });
    
    console.log("User profile saved to Firestore");
  } catch (error) {
    console.error("Error saving user profile: ", error);
    throw error;
  }
};

/**
 * Retrieves a user's profile from Firestore
 * @param {string} uid - The user's UID
 * @returns {Promise<Object|null>} - The user profile or null if not found
 */
export const getUserProfile = async (uid) => {
  if (!uid) return null;
  
  // Check rate limit for read operations
  
  try {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    } else {
      console.log("No user profile found in Firestore");
      return null;
    }
  } catch (error) {
    console.error("Error getting user profile: ", error);
    throw error;
  }
};

// ----- Scenario Management -----

/**
 * Saves a scenario to Firestore
 * @param {string} uid - The user's UID
 * @param {Object} scenario - The scenario to save
 * @returns {Promise<string>} - The ID of the saved scenario
 */
export const saveScenario = async (uid, scenario) => {
  if (!uid || !scenario) return null;
  
  // Check rate limit for write operations
  
  try {
    // If scenario has an ID, use setDoc to create or update; otherwise, create a new one with addDoc
    // Convert ID to string to handle both number and string IDs
    const scenarioIdStr = scenario.id ? String(scenario.id) : '';
    if (scenario.id && !scenarioIdStr.startsWith('temp_')) {
      const scenarioRef = doc(db, "users", uid, "scenarios", scenarioIdStr);
      await setDoc(scenarioRef, {
        ...scenario,
        updatedAt: serverTimestamp()
      }, { merge: true });
      console.log("Scenario saved to Firestore");
      return scenario.id;
    } else {
      const scenariosRef = collection(db, "users", uid, "scenarios");
      const newScenarioRef = await addDoc(scenariosRef, {
        ...scenario,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      console.log("New scenario created in Firestore");
      return newScenarioRef.id;
    }
  } catch (error) {
    console.error("Error saving scenario: ", error);
    throw error;
  }
};

/**
 * Retrieves all scenarios for a user
 * @param {string} uid - The user's UID
 * @returns {Promise<Array>} - Array of scenario objects
 */
export const getUserScenarios = async (uid) => {
  if (!uid) return [];
  
  // Check rate limit for read operations
  
  try {
    const scenariosRef = collection(db, "users", uid, "scenarios");
    const q = query(scenariosRef, orderBy("updatedAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error getting user scenarios: ", error);
    throw error;
  }
};

/**
 * Deletes a scenario from Firestore
 * @param {string} uid - The user's UID
 * @param {string} scenarioId - The ID of the scenario to delete
 * @returns {Promise<void>}
 */
export const deleteScenario = async (uid, scenarioId) => {
  if (!uid || !scenarioId) return;
  
  try {
    await deleteDoc(doc(db, "users", uid, "scenarios", scenarioId));
    console.log("Scenario deleted from Firestore");
  } catch (error) {
    console.error("Error deleting scenario: ", error);
    throw error;
  }
};

// ----- Bio Data Management -----

/**
 * Saves the user's biographical data to Firestore
 * @param {string} uid - The user's UID
 * @param {Object} bioData - The bio data to save
 * @returns {Promise<void>}
 */
export const saveBioData = async (uid, bioData) => {
  if (!uid || !bioData) return;
  
  // Check rate limit for write operations
  
  try {
    const bioDataRef = doc(db, "users", uid, "settings", "bioData");
    await setDoc(bioDataRef, {
      ...bioData,
      updatedAt: serverTimestamp()
    }, { merge: true }); // Use merge to create if doesn't exist
    console.log("Bio data saved to Firestore");
  } catch (error) {
    console.error("Error saving bio data: ", error);
    throw error;
  }
};

/**
 * Retrieves the user's biographical data from Firestore
 * @param {string} uid - The user's UID
 * @returns {Promise<Object|null>} - The bio data or null if not found
 */
export const getBioData = async (uid) => {
  if (!uid) return null;
  
  // Check rate limit for read operations
  
  try {
    const bioDataRef = doc(db, "users", uid, "settings", "bioData");
    const bioDataSnap = await getDoc(bioDataRef);
    
    if (bioDataSnap.exists()) {
      return bioDataSnap.data();
    } else {
      console.log("No bio data found in Firestore");
      return null;
    }
  } catch (error) {
    console.error("Error getting bio data: ", error);
    throw error;
  }
};

// ----- Settings Management -----

/**
 * Saves the user's application settings to Firestore
 * @param {string} uid - The user's UID
 * @param {Object} settings - The settings to save
 * @returns {Promise<void>}
 */
export const saveSettings = async (uid, settings) => {
  if (!uid || !settings) return;
  
  try {
    const settingsRef = doc(db, "users", uid, "settings", "appSettings");
    await setDoc(settingsRef, {
      ...settings,
      updatedAt: serverTimestamp()
    }, { merge: true }); // Use merge to create if doesn't exist
    console.log("Settings saved to Firestore");
  } catch (error) {
    console.error("Error saving settings: ", error);
    throw error;
  }
};

/**
 * Retrieves the user's application settings from Firestore
 * @param {string} uid - The user's UID
 * @returns {Promise<Object|null>} - The settings or null if not found
 */
export const getSettings = async (uid) => {
  if (!uid) return null;
  
  try {
    const settingsRef = doc(db, "users", uid, "settings", "appSettings");
    const settingsSnap = await getDoc(settingsRef);
    
    if (settingsSnap.exists()) {
      return settingsSnap.data();
    } else {
      console.log("No settings found in Firestore");
      return null;
    }
  } catch (error) {
    console.error("Error getting settings: ", error);
    throw error;
  }
};

// ----- Data Import/Export -----

/**
 * Exports all user data (profile, scenarios, settings) as a single object
 * @param {string} uid - The user's UID
 * @returns {Promise<Object>} - All user data
 */
export const exportUserData = async (uid) => {
  if (!uid) return null;
  
  try {
    const profile = await getUserProfile(uid);
    const scenarios = await getUserScenarios(uid);
    const bioData = await getBioData(uid);
    const settings = await getSettings(uid);
    
    return {
      profile,
      scenarios,
      bioData,
      settings,
      exportedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error("Error exporting user data: ", error);
    throw error;
  }
};

/**
 * Imports user data from an exported data object
 * @param {string} uid - The user's UID
 * @param {Object} importData - The data to import
 * @returns {Promise<void>}
 */
export const importUserData = async (uid, importData) => {
  if (!uid || !importData) return;
  
  try {
    // Import bio data if it exists
    if (importData.bioData) {
      await saveBioData(uid, importData.bioData);
    }
    
    // Import settings if they exist
    if (importData.settings) {
      await saveSettings(uid, importData.settings);
    }
    
    // Import scenarios if they exist
    if (importData.scenarios && Array.isArray(importData.scenarios)) {
      for (const scenario of importData.scenarios) {
        // Remove existing ID to create new scenarios
        const { id, ...scenarioData } = scenario;
        await saveScenario(uid, scenarioData);
      }
    }
    
    console.log("User data imported successfully");
  } catch (error) {
    console.error("Error importing user data: ", error);
    throw error;
  }
};
